import React, {
	useState,
	useEffect,
	useRef,
	forwardRef,
	useImperativeHandle,
	useCallback,
} from 'react';
import {
	ExpressCheckoutElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';

import styled from 'styled-components';
import CreditCardForm from './CreditCardForm';
import dictionary from '../../utils/dictionary';
import { waitForPaymentIntentSucceeded } from '../../utils/waitForPaymentIntentSucceeded';
import { confirmDeferredPayment } from '../../utils/confirmDeferredPayment';
import {
	getPaymentIntentExpandedForMethodCheck,
	isUsBankAccountPaymentIntent,
	isAcssDebitPaymentIntent,
} from '../../utils/paymentIntentMethod';
import { debugPaymentsLog } from '../../utils/debugPayments';
import Receipt from './Receipt';
import { CreditCardIcon } from './icons';
import { getCheckoutEntryButtonLabel } from '../../utils/checkoutPaymentMethodOrder';

const acceptableLanguages = ['en', 'fr', 'es'];

const ButtonContainer = styled.div`
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
	justify-content: flex-start;
`;

const ModernButton = styled.button`
	width: 100%;
	padding: 16px 24px;
	border: none;
	border-radius: 12px;
	font-size: 16px;
	font-weight: 600;
	font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
		sans-serif;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	text-transform: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	min-height: 48px;

	@media (max-width: 480px) {
		padding: 14px 20px;
		font-size: 15px;
		border-radius: 10px;
		min-height: 44px;
		gap: 6px;
	}

	@media (max-width: 360px) {
		padding: 12px 16px;
		font-size: 14px;
		min-height: 40px;
	}

	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	}

	@media (hover: none) {
		&:hover {
			transform: none;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		}
	}

	&:active {
		transform: translateY(0);
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
		transform: none;
	}
`;

const CardButton = styled(ModernButton)`
	/* Mirror Stripe Elements LinkButton (Express Checkout) baseline styles. */
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	background: #000000;
	color: white;
	padding: 0 16px;
	/* Link advertises min-height 32px, but renders ~40px in our layout; match that. */
	min-height: 40px;
	max-height: 64px;
	/* Link applies border-radius via inline style (4px), overriding theme vars. */
	border-radius: 4px;
	box-shadow: none;
	font-family:
		-apple-system,
		system-ui,
		BlinkMacSystemFont,
		'SF Pro Text',
		'Helvetica Neue',
		Helvetica,
		Arial,
		sans-serif,
		'Apple Color Emoji',
		'Segoe UI Emoji',
		'Segoe UI Symbol';
	font-weight: 500;
	font-size: 16px;
	line-height: 1.25;
	transition: background-color 0.15s ease;
	/* Kill Safari/WebKit teal: tap ring, focus ring, and text selection on drag. */
	-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
	-webkit-touch-callout: none;
	-webkit-focus-ring-color: transparent;
	touch-action: manipulation;
	user-select: none;
	-webkit-user-select: none;
	-webkit-appearance: none;
	appearance: none;

	&::selection {
		background: transparent;
		color: inherit;
	}

	@media (max-width: 480px) {
		padding: 0 16px;
		min-height: 40px;
		max-height: 64px;
		font-size: 16px;
	}

	@media (max-width: 360px) {
		padding: 0 14px;
		min-height: 40px;
		max-height: 64px;
		font-size: 15px;
	}

	&:hover {
		background: #1a1a1a;
		transform: none;
		box-shadow: none;
	}

	&:active {
		background: #1a1a1a;
		transform: none;
	}

	&:focus {
		outline: none;
		outline-offset: 0;
		background: #000000;
		-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
	}

	/* Mouse focus: no ring. Keyboard: subtle dark ring only. */
	&:focus:not(:focus-visible) {
		box-shadow: none;
	}

	&:focus-visible {
		outline: none;
		outline-offset: 0;
		box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.18);
		background: #000000;
	}
`;

const PaymentRequestContainer = styled.div`
	overflow: hidden;
	min-height: 48px;
	width: 100%;
	background-color: transparent;

	& > div {
		width: 100%;
	}

	@media (max-width: 480px) {
		min-height: 44px;
	}

	@media (max-width: 360px) {
		min-height: 40px;
	}
`;

const WalletErrorMessage = styled.div`
	color: #dc2626;
	font-size: 14px;
	font-weight: 500;

	@media (max-width: 480px) {
		font-size: 13px;
	}
`;

const ELEMENT_OPTIONS = {
	buttonType: {
		applePay: 'check-out',
		googlePay: 'buy',
	},
	layout: {
		maxColumns: 1,
		// Avoid Stripe collapsing wallets behind "See more" when multiple
		// Express Checkout options are available.
		maxRows: 3,
	},
	paymentMethods: {
		link: 'never',
	},
};

const PaymentButtons = forwardRef(
	(
		{
			amount,
			businessName,
			country,
			currency,
			brandColor,
			clientSecret,
			language,
			_invoice,
			invoiceData,
			customerName,
			customerEmail,
			onBackButtonStateChange,
			onPaymentIntentUpdated,
			checkoutPaymentMethodTypes,
		},
		ref
	) => {
		const stripe = useStripe();
		const elements = useElements();

		const [checkoutOutcome, setCheckoutOutcome] = useState(null);
		const [usingCreditCard, setCreditCard] = useState(false);
		const [disabled, setDisabled] = useState(false);
		const [walletError, setWalletError] = useState(null);
		const mountedRef = useRef(true);

		useEffect(() => {
			mountedRef.current = true;
			return () => {
				mountedRef.current = false;
			};
		}, []);

		const dict =
			dictionary[acceptableLanguages.includes(language) ? language : 'en'];
		const checkoutEntryLabel = getCheckoutEntryButtonLabel(
			dict,
			checkoutPaymentMethodTypes,
		);

		// Expose setCreditCard method to parent component
		useImperativeHandle(
			ref,
			() => ({
				setCreditCard: (value) => {
					setCreditCard(value);
				},
			}),
			[],
		);

		// Notify parent component when credit card form state changes
		useEffect(() => {
			if (onBackButtonStateChange) {
				onBackButtonStateChange(usingCreditCard);
			}
		}, [usingCreditCard, onBackButtonStateChange]);

		const {
			paymentSuccessTitle,
			paymentSuccessBody,
			achPendingTitle,
			achPendingBody,
			directDebitPendingTitle,
			directDebitPendingBody,
		} = dict;

		const CHEKKIT_GREEN = '#03B856';
		const canRenderExpressCheckout =
			typeof window !== 'undefined' &&
			window.location &&
			window.location.protocol === 'https:';

		const finalizeWalletPayment = useCallback((paymentIntent) => {
			if (!paymentIntent || paymentIntent.status !== 'succeeded') {
				return false;
			}
			if (onPaymentIntentUpdated) {
				onPaymentIntentUpdated(paymentIntent);
			}
			setCheckoutOutcome('paid');
			if (onBackButtonStateChange) {
				onBackButtonStateChange(false, true);
			}
			return true;
		}, [onPaymentIntentUpdated, onBackButtonStateChange]);

		const finalizeWalletPendingBank = useCallback((paymentIntent, bankKind) => {
			if (onPaymentIntentUpdated && paymentIntent) {
				onPaymentIntentUpdated(paymentIntent);
			}
			setCheckoutOutcome(bankKind === 'acss' ? 'pendingAcss' : 'pendingAch');
			if (onBackButtonStateChange) {
				onBackButtonStateChange(false, true);
			}
			return true;
		}, [onPaymentIntentUpdated, onBackButtonStateChange]);

		// Restore bank-flow “processing” state on refresh.
		// Without this, local UI state resets and the user can re-enter payment details.
		useEffect(() => {
			const pi = invoiceData && invoiceData.paymentIntent;
			if (!pi) {
				return;
			}
			if (checkoutOutcome) {
				return;
			}
			if (usingCreditCard) {
				return;
			}
			if (pi.status !== 'processing') {
				return;
			}

			if (isUsBankAccountPaymentIntent(pi)) {
				finalizeWalletPendingBank(pi, 'ach');
			} else if (isAcssDebitPaymentIntent(pi)) {
				finalizeWalletPendingBank(pi, 'acss');
			} else {
				setCreditCard(true);
			}
		}, [invoiceData, checkoutOutcome, usingCreditCard, finalizeWalletPendingBank]);

		const onExpressCheckoutConfirm = async (event) => {
			// Without this the Apple Pay / Google Pay sheet keeps spinning until it times out.
			const failWalletSheet = () => {
				if (event && typeof event.paymentFailed === 'function') {
					event.paymentFailed({ reason: 'fail' });
				}
			};

			if (!stripe || !elements) {
				failWalletSheet();
				return;
			}

			setDisabled(true);
			setWalletError(null);

			try {
				const submitResult = await elements.submit();
				if (submitResult && submitResult.error) {
					debugPaymentsLog('payments:expressCheckout elements.submit error', submitResult.error);
					failWalletSheet();
					setWalletError(
						submitResult.error.message ||
							'Payment details could not be submitted. Please try again.',
					);
					return;
				}

				const { error, paymentIntent } = await confirmDeferredPayment({
					stripe,
					elements,
					clientSecret,
				});

				if (error) {
					debugPaymentsLog('payments:expressCheckout confirmPayment error', error);
					setWalletError(
						error.message || 'Payment could not be completed. Please try again.',
					);
					return;
				}

				if (finalizeWalletPayment(paymentIntent)) {
					return;
				}

				if (paymentIntent && paymentIntent.status === 'processing') {
					let piForMethod = await getPaymentIntentExpandedForMethodCheck(
						stripe,
						clientSecret,
						paymentIntent,
					);
					if (isUsBankAccountPaymentIntent(piForMethod)) {
						finalizeWalletPendingBank(piForMethod, 'ach');
						return;
					}
					if (isAcssDebitPaymentIntent(piForMethod)) {
						finalizeWalletPendingBank(piForMethod, 'acss');
						return;
					}

					const outcome = await waitForPaymentIntentSucceeded(
						stripe,
						clientSecret,
						{ shouldAbort: () => !mountedRef.current },
					);
					if (outcome.aborted) {
						return;
					}
					if (outcome.error) {
						debugPaymentsLog('payments:expressCheckout processing poll error', outcome.error);
						setWalletError(
							outcome.error.message ||
								'Payment could not be completed. Please try again.',
						);
						return;
					}
					if (outcome.paymentIntent && finalizeWalletPayment(outcome.paymentIntent)) {
						return;
					}
					if (outcome.failed) {
						debugPaymentsLog('payments:expressCheckout processing failed', outcome);
						setWalletError('Payment was not completed. Please try again.');
						return;
					}
					if (outcome.timeout) {
						debugPaymentsLog('payments:expressCheckout processing timeout', outcome);
						piForMethod = await getPaymentIntentExpandedForMethodCheck(
							stripe,
							clientSecret,
							outcome.paymentIntent || paymentIntent,
						);
						if (
							piForMethod &&
							piForMethod.status === 'processing' &&
							isUsBankAccountPaymentIntent(piForMethod)
						) {
							finalizeWalletPendingBank(piForMethod, 'ach');
							return;
						}
						if (
							piForMethod &&
							piForMethod.status === 'processing' &&
							isAcssDebitPaymentIntent(piForMethod)
						) {
							finalizeWalletPendingBank(piForMethod, 'acss');
							return;
						}
						setWalletError(
							'Payment is still processing. Please refresh this page in a moment.',
						);
						return;
					}
				}

				setWalletError('Payment could not be completed. Please try again.');
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('payments:expressCheckout unhandled error', err);
				failWalletSheet();
				setWalletError('Payment could not be completed. Please try again.');
			} finally {
				if (mountedRef.current) {
					setDisabled(false);
				}
			}
		};

		if (checkoutOutcome) {
			const pendingAch = checkoutOutcome === 'pendingAch';
			const pendingAcss = checkoutOutcome === 'pendingAcss';
			const pendingBank = pendingAch || pendingAcss;
			const bannerTitle = pendingAch
				? achPendingTitle
				: pendingAcss
					? directDebitPendingTitle
					: paymentSuccessTitle;
			const bannerBody = pendingAch
				? achPendingBody
				: pendingAcss
					? directDebitPendingBody
					: paymentSuccessBody;
			const titleColor = pendingBank ? '#92400e' : '#166534';
			const bodyColor = pendingBank ? '#a16207' : '#166534';
			const iconBg = pendingBank ? '#f59e0b' : CHEKKIT_GREEN;

			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 0,
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-start',
							marginBottom: '12px',
							padding: '12px 14px',
							background: pendingBank
								? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
								: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
							borderRadius: '12px',
							border: pendingBank ? '1px solid #fcd34d' : '1px solid #bbf7d0',
							gap: '10px',
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: '36px',
								height: '36px',
								borderRadius: '8px',
								background: iconBg,
								flexShrink: 0,
							}}
						>
							{pendingBank ? (
								<svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
									<circle
										cx='12'
										cy='12'
										r='9'
										stroke='white'
										strokeWidth='2'
									/>
									<path
										d='M12 7v5l3 2'
										stroke='white'
										strokeWidth='2'
										strokeLinecap='round'
									/>
								</svg>
							) : (
								<svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
									<path
										d='M9 12L11 14L15 10'
										stroke='white'
										strokeWidth='2.5'
										strokeLinecap='round'
										strokeLinejoin='round'
									/>
								</svg>
							)}
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<h4
								style={{
									color: titleColor,
									margin: '0 0 2px 0',
									fontSize: '15px',
									fontWeight: '600',
									lineHeight: 1.3,
									fontFamily:
										'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								}}
							>
								{bannerTitle}
							</h4>
							<p
								style={{
									color: bodyColor,
									margin: '0',
									fontSize: '13px',
									fontWeight: '400',
									lineHeight: 1.4,
									fontFamily:
										'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								}}
							>
								{bannerBody}
							</p>
						</div>
					</div>
					<div
						style={{
						minHeight: pendingBank ? 0 : 56,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'flex-start',
						}}
					>
						{!pendingBank ? (
							<Receipt
								invoiceData={invoiceData}
								language={language}
								_invoice={_invoice}
								currency={currency}
							/>
						) : null}
					</div>
				</div>
			);
		}

		// when user successfully submits form on checkout, render download receipt button
		const onCheckoutSubmit = ({ success: checkoutSuccess, pendingBank }) => {
			if (pendingBank === 'ach') {
				setCheckoutOutcome('pendingAch');
				if (onBackButtonStateChange) {
					onBackButtonStateChange(false, true);
				}
				return;
			}
			if (pendingBank === 'acss') {
				setCheckoutOutcome('pendingAcss');
				if (onBackButtonStateChange) {
					onBackButtonStateChange(false, true);
				}
				return;
			}
			if (checkoutSuccess) {
				setCheckoutOutcome('paid');
				if (onBackButtonStateChange) {
					onBackButtonStateChange(false, true);
				}
			}
		};
		if (usingCreditCard) {
			return (
				<ButtonContainer>
					<CreditCardForm
						key={clientSecret}
						currency={currency}
						amount={amount}
						brandColor={brandColor}
						clientSecret={clientSecret}
						customerName={customerName}
						customerEmail={customerEmail}
						country={country}
						disabled={disabled}
						onCheckoutSubmit={onCheckoutSubmit}
						onPaymentIntentUpdated={onPaymentIntentUpdated}
						language={language}
						checkoutPaymentMethodTypes={checkoutPaymentMethodTypes}
						suppressPaymentElementWallets={canRenderExpressCheckout}
					/>
				</ButtonContainer>
			);
		}

		const checkoutBusy = disabled;

		const expressCheckoutEl =
			stripe && elements && canRenderExpressCheckout ? (
				<React.Fragment>
					<PaymentRequestContainer>
						<ExpressCheckoutElement
							options={ELEMENT_OPTIONS}
							onConfirm={onExpressCheckoutConfirm}
							onLoadError={(err) => {
								debugPaymentsLog('payments:expressCheckout load error', err);
							}}
						/>
					</PaymentRequestContainer>
					{walletError && (
						<WalletErrorMessage role='alert'>{walletError}</WalletErrorMessage>
					)}
				</React.Fragment>
			) : null;

		// Single entry point; the label reflects the enabled methods (card-only vs bank-only vs both).
		return (
			<ButtonContainer>
				{expressCheckoutEl}
				<CardButton
					type='button'
					brandColor={brandColor}
					onClick={() => setCreditCard(true)}
					disabled={checkoutBusy}
				>
					<CreditCardIcon />
					{checkoutEntryLabel}
				</CardButton>
			</ButtonContainer>
		);

	}
);

export default PaymentButtons;
